import React from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Switch } from '@mui/material';

import { closeDialog } from 'app/store/fuse/dialogSlice';
import { Icon, SquareChip, useSettings, Utils } from 'app/main/components';
import { DetailsSectionWrapper } from 'app/main/components/page-layouts';
import { showDailyReportParametersDialog } from 'app/main/modules/users/details/sections/components/DailyReportParametersDialog';

export default function UsersDetailsSectionReports({ model, isEdit }: { model: any; isEdit: boolean }) {
	const { t } = useSettings();
	const dispatch = useDispatch();
	const intl = useIntl();

	const [reportDailyEnabled, setReportDailyEnabled] = React.useState(model.user_dailyreport_enabled || false);
	const [reportDailyId, setReportDailyId] = React.useState(model.user_dailyreport_uid);
	const [reportDailyInfo, setReportDailyInfo] = React.useState<any | null>(null);

	React.useEffect(() => {
		if (!reportDailyId) return;
		Utils.executeApi.get({
			url: `api/jobs/${reportDailyId}`,
			dispatch,
			successHandler: ({ data }) => setReportDailyInfo(data)
		});
	}, [dispatch, reportDailyId]);

	const updateReportDailyEnabledDB = job => {
		Utils.executeStored({
			action: 'st_users_update',
			dispatch,
			data: {
				user_uid: model.user_uid,
				user_user_modified_uid: model.user_uid,
				user_dailyreport_enabled: !reportDailyEnabled,
				user_dailyreport_uid: job?.id || null
			},
			successHandler: () => {
				dispatch(closeDialog());
				setReportDailyEnabled(state => !state);
				setReportDailyId(job?.id || null);
				setReportDailyInfo(job || null);
				dispatch(Utils.alertSuccessMsg(t));
			}
		});
	};

	const changeReportDailyEnabled = () => {
		if (!reportDailyEnabled) {
			dispatch(
				showDailyReportParametersDialog({
					userUid: model.user_uid,
					userMail: model.user_mail,
					updateReportDailyEnabledDB
				})
			);
		} else {
			dispatch(Utils.alertProgressMsg(t));
			Utils.executeApi.delete({
				url: `api/jobs/${reportDailyId}`,
				dispatch,
				successHandler: () => updateReportDailyEnabledDB(null),
				errorHandler: ex => {
					if (ex?.status === 404) updateReportDailyEnabledDB(null);
					else dispatch(Utils.alertApiError(ex));
				}
			});
		}
	};

	return (
		<DetailsSectionWrapper
			title={t('USERS_DAILYREPORT_SECTION')}
			subheader={t('USERS_DAILYREPORT_SUBHEADER')}
			avatar={<Icon icon="email-fast" />}
			headerAction={
				<Switch checked={reportDailyEnabled} onChange={changeReportDailyEnabled} disabled={!isEdit} />
			}
		>
			{reportDailyEnabled && reportDailyInfo && (
				<>
					<SquareChip
						label={intl.formatMessage(
							{ id: 'dailyReport.template', defaultMessage: t('DAILY_REPORT_TEMPLATE') },
							{ template: reportDailyInfo.data.templateName }
						)}
					/>
					<SquareChip
						label={intl.formatMessage(
							{ id: 'dailyReport.recurrence', defaultMessage: t('DAILY_REPORT_RECURRENCE') },
							{ cron: reportDailyInfo.cron }
						)}
					/>
					<SquareChip
						label={intl.formatMessage(
							{ id: 'dailyReport.createdAt', defaultMessage: t('DAILY_REPORT_CREATEDAT') },
							{
								date: intl.formatDate(Utils.parseDate(reportDailyInfo.createdAt)?.toDate(), {
									year: 'numeric',
									month: '2-digit',
									day: '2-digit',
									hour: '2-digit',
									minute: 'numeric'
								})
							}
						)}
					/>
					<SquareChip
						label={intl.formatMessage(
							{ id: 'dailyReport.nextExecution', defaultMessage: t('DAILY_REPORT_NEXTEXECUTION') },
							{
								date: intl.formatDate(Utils.parseDate(reportDailyInfo.nextExecution)?.toDate(), {
									year: 'numeric',
									month: '2-digit',
									day: '2-digit',
									hour: '2-digit',
									minute: 'numeric'
								})
							}
						)}
					/>
				</>
			)}
		</DetailsSectionWrapper>
	);
}
